import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { Badge } from '../../components/ui/Badge.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { FieldGrid } from '../../components/ui/FieldGrid.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { formatCurrency, formatDate, formatNumber } from '../../utils/formatters.js';

const STATUS_VARIANT = {
  MATCHED: 'success',
  OVER_BILLED: 'danger',
  UNDER_BILLED: 'warning',
  NO_RECEIPT: 'neutral',
};

/** True when the invoice is a purchase invoice linked to a purchase order. */
export function isPoLinkedPurchase(invoice) {
  return Boolean(
    invoice
      && invoice.type === 'PURCHASE'
      && invoice.sourceType === 'PURCHASE_ORDER'
      && invoice.sourceId,
  );
}

/**
 * 3-way-match card on the invoice detail page: compares the billed net amount
 * against the goods-receipt snapshot of the linked purchase order.
 */
export function InvoiceMatchCard({ invoice }) {
  const { t } = useTranslation();
  const applicable = isPoLinkedPurchase(invoice);

  const match = useQuery({
    queryKey: ['finance', 'invoices', invoice?.id, 'match'],
    queryFn: () => financeApi.invoiceMatch(invoice.id),
    enabled: applicable,
  });

  if (!applicable || !match.data) return null;
  const m = match.data;
  if (m.status === 'NOT_APPLICABLE') return null;

  const hasReceipt = m.status !== 'NO_RECEIPT';
  const diff = Number(m.difference || 0);
  const diffColor = diff > 0
    ? 'var(--color-danger)'
    : diff < 0
      ? 'var(--color-warning)'
      : 'var(--color-success)';

  return (
    <Card
      title={t('match.title')}
      actions={
        <span style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
          <Badge variant={STATUS_VARIANT[m.status] || 'neutral'}>
            {t(`match.status.${m.status}`)}
          </Badge>
          <Link to={`/inventory/purchase-orders/${invoice.sourceId}`}>
            {m.poNumber || invoice.sourceId.slice(0, 8)}
          </Link>
        </span>
      }
    >
      <FieldGrid items={[
        ...(hasReceipt
          ? [
              { label: t('match.receivedAt'), value: formatDate(m.receivedAt) },
              {
                label: t('match.receivedValue'),
                value: <span className="mono">{formatCurrency(m.receivedValue, invoice.currency)}</span>,
              },
              {
                label: t('match.billedNet'),
                value: <span className="mono">{formatCurrency(m.billedNet, invoice.currency)}</span>,
              },
              {
                label: t('match.difference'),
                value: (
                  <strong className="mono" style={{ color: diffColor }}>
                    {formatCurrency(m.difference, invoice.currency)}
                  </strong>
                ),
              },
            ]
          : [
              {
                label: t('col.status'),
                value: <span className="muted">{t('match.noReceiptHint')}</span>,
              },
            ]),
      ]} />

      {hasReceipt && (m.lines || []).length > 0 && (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <Table
            rows={m.lines}
            getRowKey={(row) => row.productId || row.description}
            columns={[
              { key: 'description', label: t('detail.lineDescription') },
              { key: 'quantity', label: t('detail.lineQty'), align: 'right', render: (r) => formatNumber(r.quantity) },
              { key: 'unitCost', label: t('match.unitCost'), align: 'right', render: (r) => formatCurrency(r.unitCost, invoice.currency) },
              { key: 'lineAmount', label: t('match.lineValue'), align: 'right', render: (r) => formatCurrency(r.lineAmount, invoice.currency) },
            ]}
          />
        </div>
      )}
    </Card>
  );
}
